import { FC, useContext, useState } from 'react';
import { MainLogo } from '@benzinga/logos-ui';
import Link from 'next/link';
import { Login } from '../Login';
import { Register } from '../Register';
import { SMSVerify } from '../SMSVerify';
import { Complete } from '../Complete';
import { Upsell } from '../Upsell';
import { ReportSignUp } from '../Upsell/ReportSignUp';
import { useTranslation } from 'react-i18next';
import { AuthIterationType } from './types';
import { Authentication } from '@benzinga/session';
import { SafeType } from '@benzinga/safe-await';
import { IdentityContext } from '@benzinga/identity-ui';
import { sanitizeHTML } from '@benzinga/frontend-utils';

export const DefaultIteration: FC<AuthIterationType> = ({
  authMode,
  email,
  isPaywalled = false,
  isRegwalled = false,
  onLogin,
  onRegister,
  onSocialClicked,
  phoneNumber: phone,
  setAuthMode,
}) => {
  const { config: identityConfig } = useContext(IdentityContext);
  const { t } = useTranslation(['auth', 'common']);
  const [phoneNumber, setPhoneNumber] = useState(phone);
  const [authentication, setAuthentication] = useState<SafeType<Authentication> | null>(null);
  const [socialType, setSocialType] = useState<string>('');
  const [offerEmail, setOfferEmail] = useState(email);
  const showUpsell = true;

  const onUpsellSocialClick = (socialType: string) => {
    setSocialType(socialType);
    setAuthMode && setAuthMode('upsell');
  };

  const handleRegisterPostProcess = () => {
    if (authentication) onRegister && onRegister(authentication, 'register');
  };

  const handleSocialPostProcess = () => {
    onSocialClicked && onSocialClicked(socialType);
  };

  const handleReportExit = () => {
    setAuthMode && setAuthMode('upsell');
  };

  const handleUpsellExit = () => {
    if (socialType) handleSocialPostProcess();
    else {
      setAuthMode && setAuthMode('complete');
      handleRegisterPostProcess();
    }
  };

  return (
    <div
      className={`flex flex-col max-w-[520px] size-full md:size-auto border border-black no-scrollbar overflow-scroll flex flex-col md:flex-row md:gap-12 fixed z-50 bg-blue-950 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 shadow-xl ${authMode === 'report-offer' ? 'border-bzblue-600' : 'p-6 md:p-8 md:pb-8'}`}
    >
      <div className="text-white">
        {authMode !== 'upsell' && authMode !== 'report-offer' && (
          <Link href="/" prefetch={false}>
            <MainLogo logoVariant={identityConfig.logoVariant} variant="light" />
          </Link>
        )}
        {authMode === 'login' || authMode === 'register' ? (
          <>
            <h3 className="text-4xl text-white mb-4">{t('Auth.AuthModal.login.get-unlimited-access')}</h3>
            <p className="text-white">
              <span dangerouslySetInnerHTML={{ __html: sanitizeHTML(t('Auth.AuthModal.login.come-see', { ns: 'auth' })) }} />{' '}
            </p>
            <p className="max-w-[400px]">
              <a className="underline text-blue-500" href="/reports/TSLA" rel="noopener noreferrer" target="_blank">
                {t('Auth.AuthModal.Benefits.in-depth-reports', { ns: 'auth' })}
              </a>
              ,{' '}
              <a
                className="underline text-blue-500"
                href="/analyst-stock-ratings"
                rel="noopener noreferrer"
                target="_blank"
              >
                {t('Auth.AuthModal.Benefits.analyst-ratings', { ns: 'auth' })}
              </a>
              ,{' '}
              <a className="underline text-blue-500" href="/earnings" rel="noopener noreferrer" target="_blank">
                {t('Auth.AuthModal.Benefits.earnings', { ns: 'auth' })}
              </a>
              , {t('Auth.AuthModal.Benefits.and-more', { ns: 'auth' })}{' '}
              {t('Auth.AuthModal.Benefits.or-return-to', { ns: 'auth' })}{' '}
              <a className="underline text-blue-500" href="/" rel="noopener noreferrer" target="_blank">
                Benzinga.com
              </a>
            </p>
            <div className="max-w-[500px]">
              {authMode === 'login' && (
                <div className="select-none">
                  <Login
                    isPaywalled={isPaywalled}
                    isRegwalled={isRegwalled}
                    onLogin={onLogin}
                    onSocialClicked={onSocialClicked}
                    setAuthMode={setAuthMode}
                  />
                </div>
              )}
              {authMode === 'register' && (
                <div className="select-none" id="register-form">
                  <Register
                    initialEmail={email}
                    isPaywalled={isPaywalled}
                    isRegwalled={isRegwalled}
                    onRegister={onRegister}
                    onSocialClicked={showUpsell ? onUpsellSocialClick : onSocialClicked}
                    setAuthentication={setAuthentication}
                    setAuthMode={setAuthMode}
                    setOfferEmail={setOfferEmail}
                    setPhoneNumber={setPhoneNumber}
                    showUpsell={showUpsell}
                  />
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="max-w-[500px]">
            {authMode === 'sms-verify' && (
              <SMSVerify
                authentication={authentication}
                onVerified={onRegister}
                phoneNumber={phoneNumber}
                setAuthMode={setAuthMode}
              />
            )}
            {authMode === 'complete' && <Complete />}
            {authMode === 'report-offer' && <ReportSignUp authEmail={offerEmail} onClose={handleReportExit} />}
            {authMode === 'upsell' && <Upsell onClose={handleUpsellExit} />}
          </div>
        )}
      </div>
    </div>
  );
};
